import { useEffect, useMemo, useState } from 'react'

import type { BookAiSection, BookAiStatus } from '@/api/services/book-ai'
import { useBookAiPurposeStore, useCurrentPurpose } from '@/store/useBookAiPurposeStore'
import { isBookAiJobActive, useBookAiStore } from '@/store/useBookAiStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { collectKeySentencesNear, findSectionIndex, type KeySentence } from './book-ai-position'

// 解析中に進捗を取り直す間隔
const POLL_INTERVAL_MS = 4000
// 章に入ったときの「この章のポイント」を出しておく時間
const PREVIEW_CHIP_MS = 8000

const EMPTY_SECTIONS: BookAiSection[] = []
const SENTENCE_SEPARATOR = '\u0000'
const LEVEL_SEPARATOR = '\u0001'

export type BookAiReaderState = {
  episodeId: number
  status: BookAiStatus | null
  sections: BookAiSection[]
  /** 今読んでいる位置キー（不明なら null） */
  currentKey: number | null
  /** 今読んでいる章の番号（不明なら -1） */
  currentSectionIndex: number
  isJobActive: boolean
}

/**
 * リーダーで開いている本の AI 読書アシストの状態
 * @param currentKey 今読んでいる位置キー（EPUB: spine 番号 + 進み具合 / PDF: 0 始まりのページ）
 */
export const useBookAi = (episodeId: number, currentKey: number | null): BookAiReaderState => {
  const status = useBookAiStore((state) => (state.episodeId === episodeId ? state.status : null))
  const open = useBookAiStore((state) => state.open)
  const refreshStatus = useBookAiStore((state) => state.refreshStatus)

  const openPurpose = useBookAiPurposeStore((state) => state.open)
  useEffect(() => {
    open(episodeId)
    openPurpose(episodeId)
  }, [episodeId, open, openPurpose])

  // 目的を先に決めてから解析した場合、本文の埋め込みができたら目的に効く箇所を取り直す
  const chatReady = !!status?.chat_ready
  const hasPurposeWithoutMap = useBookAiPurposeStore((state) => !!state.purpose && !state.purpose.map)
  const reloadPurpose = useBookAiPurposeStore((state) => state.reload)
  useEffect(() => {
    if (chatReady && hasPurposeWithoutMap) reloadPurpose()
  }, [chatReady, hasPurposeWithoutMap, reloadPurpose])

  const isJobActive = isBookAiJobActive(status)
  useEffect(() => {
    if (!isJobActive) return
    const timer = setInterval(refreshStatus, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [isJobActive, refreshStatus])

  const sections = status?.sections ?? EMPTY_SECTIONS
  const currentSectionIndex = useMemo(() => findSectionIndex(sections, currentKey), [sections, currentKey])

  return { episodeId, status, sections, currentKey, currentSectionIndex, isJobActive }
}

/**
 * 流し読みで強調する文（今の位置の前後 margin にかかる章の分。設定した重要度まで）
 *
 * 解析中は数秒ごとに状態を取り直すため、内容が同じなら同じ配列を返し、本文の照合をやり直させない。
 */
export const useSkimSentences = (bookAi: BookAiReaderState, margin: number): Pick<KeySentence, 'text' | 'level'>[] => {
  const maxLevel = useUISettingsStore((state) => state.readerSkimLevel)
  const joined = useMemo(
    () =>
      collectKeySentencesNear(bookAi.sections, bookAi.currentKey, margin, maxLevel)
        .map((s) => `${s.level}${LEVEL_SEPARATOR}${s.text}`)
        .join(SENTENCE_SEPARATOR),
    [bookAi.sections, bookAi.currentKey, margin, maxLevel],
  )
  return useMemo(() => {
    if (!joined) return []
    return joined.split(SENTENCE_SEPARATOR).map((item) => {
      const [level, text] = item.split(LEVEL_SEPARATOR)
      return { text: text ?? '', level: level === '1' ? 1 : level === '3' ? 3 : 2 }
    })
  }, [joined])
}

/**
 * 章に入ったときに一定時間だけ「この章のポイント」を出す章の番号（出さないときは null）
 */
export const useSectionPreviewChip = (bookAi: BookAiReaderState, suppressed: boolean): number | null => {
  const enabled = useUISettingsStore((state) => state.readerSectionPreview)
  const [shown, setShown] = useState<{ index: number; visible: boolean } | null>(null)

  const index = bookAi.currentSectionIndex
  const hasAnalysis = bookAi.sections.some((s) => s.index === index && s.analysis)
  // 章が変わったら出し直す（描画中に状態を合わせる）
  if (enabled && hasAnalysis && shown?.index !== index) {
    setShown({ index, visible: true })
  }

  useEffect(() => {
    if (!shown?.visible) return
    const timer = setTimeout(() => setShown((prev) => prev && { ...prev, visible: false }), PREVIEW_CHIP_MS)
    return () => clearTimeout(timer)
  }, [shown])

  if (!enabled || suppressed || !shown?.visible || shown.index !== index) return null
  return shown.index
}

/** 読む目的に関係する本文の断片と、その位置キー（EPUB: spine 番号 + 進み具合 / PDF: 断片が始まるページ） */
export type PurposePassageMark = { text: string; key: number }

/**
 * 読む目的に関係する本文の断片のうち、今の位置の前後 margin にかかるもの（本文に印を付ける）
 * margin が Infinity なら位置に関わらず全部（連続スクロールの EPUB は今の位置の推定がずれやすく、
 * 近くだけに絞ると、スクロールで印が出たり消えたりするため）
 *
 * 内容が同じなら同じ配列を返し、本文の照合をやり直させない。
 */
export const usePurposePassages = (bookAi: BookAiReaderState, margin: number): PurposePassageMark[] => {
  const enabled = useUISettingsStore((state) => state.readerPurposeMarks)
  const passages = useCurrentPurpose(bookAi.episodeId)?.map?.passages
  const current = bookAi.currentKey == null ? null : Math.floor(bookAi.currentKey)
  const joined = useMemo(() => {
    if (!enabled || !passages) return ''
    if (current == null && Number.isFinite(margin)) return ''
    return passages
      .filter((p) => current == null || Math.abs(Math.floor(p.key) - current) <= margin)
      .map((p) => `${p.key}${LEVEL_SEPARATOR}${p.text}`)
      .join(SENTENCE_SEPARATOR)
  }, [enabled, passages, current, margin])
  return useMemo(() => {
    if (!joined) return []
    return joined.split(SENTENCE_SEPARATOR).map((item) => {
      const [key, text] = item.split(LEVEL_SEPARATOR)
      return { text: text ?? '', key: Number(key) }
    })
  }, [joined])
}

/** シークバーに印を付ける、読む目的に関係する本文の断片の位置キー（印を付けないときは空） */
export const usePurposePassageKeys = (episodeId: number): number[] => {
  const enabled = useUISettingsStore((state) => state.readerPurposeMarks)
  const passages = useCurrentPurpose(episodeId)?.map?.passages
  return useMemo(() => (enabled && passages ? passages.map((p) => p.key) : []), [enabled, passages])
}
